/**
 * German operator-facing status labels (Welle 4.6 QoL-6).
 *
 * Centralises the enum-to-label maps so every SPA page renders
 * the same friendly label for the same status. Each lookup uses
 * `LABEL_MAP[value] ?? value` so an unknown value (e.g. a future
 * backend enum the SPA hasn't been redeployed against) renders
 * the raw key as a graceful fallback rather than `undefined`.
 *
 * Color maps stay local to each page — they're tied to the page's
 * visual rhythm and don't benefit from sharing.
 */
import type { FederationStatus, PlatformCredentialStatus } from './api';

export const FEDERATION_STATUS_LABEL: Record<FederationStatus, string> = {
  reachable: 'Erreichbar',
  unreachable: 'Nicht erreichbar',
  auth_failed: 'Auth fehlgeschlagen',
  timeout: 'Timeout',
  disabled: 'Deaktiviert',
  unknown: 'Unbekannt',
  local: 'Lokal',
};

export function labelForFederationStatus(s: string): string {
  return FEDERATION_STATUS_LABEL[s as FederationStatus] ?? s;
}

/**
 * Tenant lifecycle status values written by `tenant.service` and
 * surfaced on TenantListPage / TenantDetailPage. Source of truth
 * is `Tenant.status` in the Prisma schema.
 */
export const TENANT_STATUS_LABEL: Record<string, string> = {
  active: 'Aktiv',
  provisioning: 'Wird provisioniert',
  suspending: 'Wird ausgesetzt',
  suspended: 'Ausgesetzt',
  reactivating: 'Wird reaktiviert',
  failed: 'Fehlgeschlagen',
  deprovisioning: 'Wird abgebaut',
  decommissioning: 'Wird abgebaut',
  decommissioned: 'Abgebaut',
  decommission_failed: 'Abbau fehlgeschlagen',
};

export function labelForTenantStatus(s: string): string {
  return TENANT_STATUS_LABEL[s] ?? s;
}

/**
 * AWX job lifecycle status values from `AwxJob.status`. Mirrors
 * AWX's own status vocabulary (`pending`/`waiting`/`running`/
 * `successful`/`failed`/`error`/`canceled`).
 */
export const AWX_JOB_STATUS_LABEL: Record<string, string> = {
  pending: 'Warten',
  waiting: 'Wartend',
  running: 'Läuft',
  successful: 'Erfolgreich',
  failed: 'Fehlgeschlagen',
  error: 'Fehler',
  canceled: 'Abgebrochen',
};

export function labelForAwxJobStatus(s: string): string {
  return AWX_JOB_STATUS_LABEL[s] ?? s;
}

/**
 * MSP-Kontext-Status (`ProvisioningContext.status`). Ein archivierter
 * Kontext behält seine Historie, nimmt aber keine neuen Mandanten mehr auf.
 */
export const CONTEXT_STATUS_LABEL: Record<string, string> = {
  active: 'Aktiv',
  archived: 'Archiviert',
};

export function labelForContextStatus(s: string): string {
  return CONTEXT_STATUS_LABEL[s] ?? s;
}

/** Kunden-Status (`Customer.status`); archiviert nimmt keine neuen Deployments auf. */
export const CUSTOMER_STATUS_LABEL: Record<string, string> = {
  active: 'Aktiv',
  archived: 'Archiviert',
};

export function labelForCustomerStatus(s: string): string {
  return CUSTOMER_STATUS_LABEL[s] ?? s;
}

/** Marken-Status (`Brand.status`). */
export const BRAND_STATUS_LABEL: Record<string, string> = {
  active: 'Aktiv',
  archived: 'Archiviert',
};

export function labelForBrandStatus(s: string): string {
  return BRAND_STATUS_LABEL[s] ?? s;
}

/** Vertrags-Status (`TenantContract.status`); suspended blockiert neue Allokation. */
export const CONTRACT_STATUS_LABEL: Record<string, string> = {
  active: 'Aktiv',
  suspended: 'Ausgesetzt',
};

export function labelForContractStatus(s: string): string {
  return CONTRACT_STATUS_LABEL[s] ?? s;
}

/**
 * Lizenz-Status (`TenantLicense.status` + der synthetische `none`-Default,
 * den der Service liefert, wenn keine Lizenz gesetzt ist).
 */
export const LICENSE_STATUS_LABEL: Record<string, string> = {
  none: 'Keine Lizenz',
  active: 'Aktiv',
  suspended: 'Ausgesetzt',
};

export function labelForLicenseStatus(s: string): string {
  return LICENSE_STATUS_LABEL[s] ?? s;
}

/**
 * Status eines externen Zugangsdaten-Eintrags (Seite „Zugangsdaten“),
 * geliefert von `GET /api/platform-credentials`.
 */
export const PLATFORM_CREDENTIAL_STATUS_LABEL: Record<PlatformCredentialStatus, string> = {
  complete: 'Vollständig',
  partial: 'Unvollständig',
  missing: 'Fehlt',
  unknown: 'Unbekannt',
};

export function labelForPlatformCredentialStatus(s: string): string {
  return PLATFORM_CREDENTIAL_STATUS_LABEL[s as PlatformCredentialStatus] ?? s;
}
